import React, { useState, useEffect, useCallback, useRef } from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Maximize2, Minimize2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const apparatusLabel = {
  vault: 'Vault', wagvault: 'Vault', magvault: 'Vault',
  bars: 'Bars', beam: 'Beam',
  floor: 'Floor', wagfloor: 'Floor', magfloor: 'Floor',
  range: 'R&C', randc: 'R&C',
  rings: 'Rings', pommel: 'Pommel', pbars: 'P-Bars', hbar: 'High Bar',
  aa: 'All Around', team: 'Team Competition',
};

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function PresenterPage() {
  usePageTitle('Presenter');

  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState('');
  const [competition, setCompetition] = useState(null);

  const [timer, setTimer] = useState(null);
  const [displayTime, setDisplayTime] = useState(0);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [nextUp, setNextUp] = useState(null);
  const [presentationState, setPresentationState] = useState(null);

  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const compIdRef = useRef(selectedCompetitionId);
  useEffect(() => { compIdRef.current = selectedCompetitionId; }, [selectedCompetitionId]);

  // Load competitions
  useEffect(() => {
    axios.get(`${API}/music/competitions`).then(r => {
      const comps = r.data.competitions || [];
      setCompetitions(comps);
      const saved = localStorage.getItem('presenter_competition_id');
      if (saved && comps.find(c => c.id === saved)) {
        setSelectedCompetitionId(saved);
        setCompetition(comps.find(c => c.id === saved));
      } else if (comps.length === 1) {
        setSelectedCompetitionId(comps[0].id);
        setCompetition(comps[0]);
      }
    }).catch(() => {});
  }, []);

  // Save selection
  useEffect(() => {
    if (selectedCompetitionId) {
      localStorage.setItem('presenter_competition_id', selectedCompetitionId);
      setCompetition(competitions.find(c => c.id === selectedCompetitionId) || null);
    }
  }, [selectedCompetitionId, competitions]);

  // Poll state every 1s
  const fetchState = useCallback(async () => {
    const compId = compIdRef.current;
    if (!compId) return;
    try {
      const [timerRes, nowRes, nextRes, presRes] = await Promise.all([
        axios.get(`${API}/timer/state/${compId}`).catch(() => null),
        axios.get(`${API}/now-playing/${compId}`).catch(() => null),
        axios.get(`${API}/music-queue/${compId}/next-up`).catch(() => null),
        axios.get(`${API}/presentation/state/${compId}`).catch(() => null),
      ]);

      const td = timerRes?.data?.timer;
      if (td?.isRunning) {
        setTimer(td);
        if (!td.isPaused) {
          const elapsed = Math.floor((Date.now() - new Date(td.startedAt).getTime()) / 1000);
          setDisplayTime(Math.max(0, td.duration - elapsed));
        } else {
          setDisplayTime(td.remainingTime || 0);
        }
      } else {
        setTimer(null);
        setDisplayTime(0);
      }

      setNowPlaying(nowRes?.data?.nowPlaying || null);
      setNextUp(nextRes?.data?.nextUp || null);

      const newPres = presRes?.data || null;
      setPresentationState(prev => {
        const key = s => s ? `${s.isActive}|${s.category}|${s.apparatus}|${JSON.stringify(s.revealedPlaces)}` : 'null';
        return key(prev) === key(newPres) ? prev : newPres;
      });
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (!selectedCompetitionId) return;
    fetchState();
    const id = setInterval(fetchState, 1000);
    return () => clearInterval(id);
  }, [selectedCompetitionId, fetchState]);

  const isPresenting = presentationState?.isActive && (presentationState?.rankings?.length ?? 0) > 0;
  const timerWarning = timer && displayTime <= 30 && displayTime > 0;
  const timerCritical = timer && displayTime <= 10 && displayTime > 0;

  return (
    <div
      className="min-h-screen bg-white text-black flex flex-col select-none"
      data-testid="presenter-page"
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
        <div className="flex-1" />
        <img src="/scorz-logo.png" alt="Scorz" className="h-8 opacity-80" />
        <div className="flex-1 flex justify-end">
          <div className="flex items-center gap-2">
            <button
              onClick={toggleFullscreen}
              data-testid="presenter-fullscreen-btn"
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-black transition-colors"
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <div className="w-48">
              <Select value={selectedCompetitionId} onValueChange={setSelectedCompetitionId}>
                <SelectTrigger className="bg-gray-50 border-gray-300 text-black text-xs h-8">
                  <SelectValue placeholder="Pick competition" />
                </SelectTrigger>
                <SelectContent>
                  {competitions.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {!selectedCompetitionId ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-300 text-xl">Select a competition above</p>
        </div>
      ) : isPresenting ? (
        <PresentationView
          presentationState={presentationState}
          nowPlaying={nowPlaying}
          nextUp={nextUp}
          timer={timer}
          displayTime={displayTime}
          timerWarning={timerWarning}
          timerCritical={timerCritical}
        />
      ) : (
        <NormalView
          nowPlaying={nowPlaying}
          nextUp={nextUp}
          timer={timer}
          displayTime={displayTime}
          timerWarning={timerWarning}
          timerCritical={timerCritical}
        />
      )}
    </div>
  );
}

/* ── Normal view ───────────────────────────────────────────── */
function NormalView({ nowPlaying, nextUp, timer, displayTime, timerWarning, timerCritical }) {
  return (
    <div className="flex-1 flex flex-col">
      {/* Timer strip */}
      {timer && (
        <div
          className={`flex items-center justify-center gap-3 py-4 border-b ${
            timerCritical ? 'bg-red-50 border-red-300 animate-pulse' :
            timerWarning  ? 'bg-orange-50 border-orange-200' :
                            'bg-gray-50 border-gray-200'
          }`}
          data-testid="presenter-timer"
        >
          <span className="text-gray-400 text-sm uppercase tracking-widest">Timer</span>
          <span className={`text-5xl font-black tabular-nums ${
            timerCritical ? 'text-red-600' : timerWarning ? 'text-orange-600' : 'text-black'
          }`}>
            {formatTime(displayTime)}
          </span>
          {timer.isPaused && <span className="text-amber-700 text-sm font-bold tracking-wider">PAUSED</span>}
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex gap-0 overflow-hidden">
        {/* NOW PERFORMING */}
        <div
          className="flex-1 flex flex-col items-center justify-center p-10 border-r border-gray-100"
          data-testid="presenter-now-performing"
        >
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-green-700 mb-6">
            Now Performing
          </div>
          {nowPlaying ? (
            <>
              <div
                className="text-[clamp(4rem,12vw,9rem)] font-black leading-none tabular-nums text-black mb-4"
                data-testid="presenter-now-number"
              >
                {nowPlaying.gymnastNumber}{nowPlaying.gymnastLetter || ''}
              </div>
              <div
                className="text-[clamp(1.5rem,4vw,3rem)] font-bold text-gray-900 leading-tight text-center mb-2"
                data-testid="presenter-now-name"
              >
                {nowPlaying.gymnastName}
              </div>
              <div className="text-[clamp(1rem,2.5vw,1.75rem)] text-gray-400 font-medium text-center" data-testid="presenter-now-club">
                {nowPlaying.clubName}
              </div>
            </>
          ) : (
            <div className="text-gray-200 text-2xl font-light">No one performing</div>
          )}
        </div>

        {/* NEXT UP */}
        <div
          className="w-2/5 flex flex-col items-center justify-center p-10 bg-gray-50"
          data-testid="presenter-next-up"
        >
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600 mb-6">
            Next Up
          </div>
          {nextUp ? (
            <>
              <div
                className="text-[clamp(3rem,8vw,6rem)] font-black leading-none tabular-nums text-black mb-3"
                data-testid="presenter-next-number"
              >
                {nextUp.number}{nextUp.letter || ''}
              </div>
              <div
                className="text-[clamp(1.25rem,3vw,2.25rem)] font-bold text-gray-800 leading-tight text-center mb-2"
                data-testid="presenter-next-name"
              >
                {nextUp.firstName} {nextUp.lastName}
              </div>
              <div className="text-[clamp(0.875rem,2vw,1.5rem)] text-gray-400 font-medium text-center" data-testid="presenter-next-club">
                {nextUp.clubName}
              </div>
            </>
          ) : (
            <div className="text-gray-300 text-xl font-light">
              {nowPlaying ? 'Last performer' : 'Waiting to start'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Presentation mode view — booklet style, all results at once ─── */
function PresentationView({ presentationState, nowPlaying, nextUp, timer, displayTime, timerWarning, timerCritical }) {
  const catLabel = presentationState.category || '';
  const appLabel = apparatusLabel[presentationState.apparatus] || (presentationState.apparatus || '').toUpperCase();

  const rankings = presentationState.rankings || [];

  // Group by rank to handle joint places
  const grouped = {};
  rankings.forEach(r => {
    if (r.rank == null) return;
    if (!grouped[r.rank]) grouped[r.rank] = [];
    grouped[r.rank].push(r);
  });

  // All unique ranks, sorted highest (6th) → lowest (1st) for MC build-up
  const sortedRanks = Object.keys(grouped).map(Number).sort((a, b) => b - a);

  const ordinal = r => r === 1 ? '1st' : r === 2 ? '2nd' : r === 3 ? '3rd' : `${r}th`;

  const rowStyle = rank => {
    if (rank === 1) return 'bg-yellow-50 border-yellow-300 border-2';
    if (rank === 2) return 'bg-gray-50 border-gray-300 border';
    if (rank === 3) return 'bg-orange-50 border-orange-200 border';
    return 'bg-white border-gray-100 border';
  };

  const rankColor = rank => {
    if (rank === 1) return 'text-yellow-700';
    if (rank === 2) return 'text-gray-600';
    if (rank === 3) return 'text-orange-700';
    return 'text-gray-400';
  };

  const formatScore = score => {
    if (score == null || score === '') return '—';
    const n = parseFloat(score);
    return isNaN(n) ? '—' : n.toFixed(3);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-indigo-50 border-b border-indigo-200 px-8 py-4 flex items-center justify-between shrink-0">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500 mb-1">Presentation Ready</div>
          <div className="text-2xl font-bold text-black">
            {catLabel} <span className="text-indigo-400">—</span> {appLabel}
          </div>
        </div>
        {timer && (
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
            timerCritical ? 'bg-red-50 border-red-300 animate-pulse' :
            timerWarning  ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-200'
          }`}>
            <span className="text-gray-400 text-xs uppercase tracking-widest">Timer</span>
            <span className={`text-3xl font-black tabular-nums ${timerCritical ? 'text-red-600' : timerWarning ? 'text-orange-600' : 'text-black'}`}>
              {formatTime(displayTime)}
            </span>
          </div>
        )}
      </div>

      {/* Ranked list — 6th down to 1st */}
      <div className="flex-1 overflow-y-auto px-8 py-4 space-y-2" data-testid="presenter-results-list">
        {sortedRanks.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-300 text-xl">No rankings available</p>
          </div>
        ) : (
          sortedRanks.map(rank => {
            const gymnasts = grouped[rank];
            const isJoint = gymnasts.length > 1;
            return gymnasts.map((g, i) => (
              <div
                key={`${rank}-${i}`}
                data-testid={`presenter-result-rank-${rank}-${i}`}
                className={`flex items-center gap-4 rounded-xl px-5 py-3 ${rowStyle(rank)}`}
              >
                {/* Rank badge */}
                <div className={`w-16 shrink-0 text-center font-black text-2xl ${rankColor(rank)}`}>
                  {isJoint && i === 0 ? `=${ordinal(rank)}` : isJoint ? '' : ordinal(rank)}
                </div>

                {/* Name + Club */}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-black text-xl leading-tight truncate">
                    {g.name || 'Unknown'}
                  </div>
                  {g.club && (
                    <div className="text-gray-500 text-sm truncate">{g.club}</div>
                  )}
                </div>

                {/* Score */}
                <div className={`shrink-0 font-black text-2xl tabular-nums ${rank === 1 ? 'text-yellow-700' : rank === 2 ? 'text-gray-600' : rank === 3 ? 'text-orange-700' : 'text-gray-500'}`}>
                  {formatScore(g.score)}
                </div>
              </div>
            ));
          })
        )}
      </div>

      {/* Bottom strip: now performing + next up */}
      {(nowPlaying || nextUp) && (
        <div className="border-t border-gray-200 px-6 py-3 flex gap-8 bg-gray-50 shrink-0">
          {nowPlaying && (
            <div className="flex items-center gap-3" data-testid="presenter-pres-now">
              <span className="text-green-700 text-xs font-bold uppercase tracking-widest">Now</span>
              <span className="text-black font-bold text-lg">
                #{nowPlaying.gymnastNumber}{nowPlaying.gymnastLetter || ''} {nowPlaying.gymnastName}
              </span>
              <span className="text-gray-400 text-sm">{nowPlaying.clubName}</span>
            </div>
          )}
          {nextUp && (
            <div className="flex items-center gap-3" data-testid="presenter-pres-next">
              <span className="text-amber-600 text-xs font-bold uppercase tracking-widest">Next</span>
              <span className="text-gray-700 font-bold text-lg">
                #{nextUp.number}{nextUp.letter || ''} {nextUp.firstName} {nextUp.lastName}
              </span>
              <span className="text-gray-400 text-sm">{nextUp.clubName}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
